import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useNavigate } from "react-router";
import { BeatLoader } from "react-spinners";
import { toast } from "react-toastify";
import { useCreateMutation, useIndexQuery } from "../../api/genericHooks";
import { LanguageSelectorModalButton } from "../../components/Buttons/LanguageSelectorModalButton";
import { logo } from "../../images";
import { selectUser } from "../../store/slices/auth";
import { useSelector } from "../../store/store";
import { theme } from "../../utils/theme";

type ConfirmEmailInstructionData = {
  email: string;
};

export const ConfirmEmail = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const user = useSelector(selectUser);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isResendingMode, setIsResendingMode] = useState(false);
  const navigate = useNavigate();

  // Get the confirmation_token from the query string
  const confirmation_token = new URLSearchParams(search).get(
    "confirmation_token"
  );

  // Query Methods
  useIndexQuery(
    `/confirmation`,
    confirmation_token !== null ? { confirmation_token } : undefined,
    {
      enabled: !!confirmation_token && !isConfirmed,
      onSuccess: () => {
        setIsConfirmed(true);
        toast.success(t("confirmEmail.emailConfirmed"));
        navigate("/login");
      },
    }
  );

  const confirmEmailInstructionsQuery = useCreateMutation("/confirmation");

  useEffect(() => {
    if (user?.is_confirmed) {
      setIsConfirmed(true);
      navigate("/dashboard");
    }
  }, [user]);

  // Form Methods
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ConfirmEmailInstructionData>();

  const onSubmit: SubmitHandler<ConfirmEmailInstructionData> = (data) => {
    confirmEmailInstructionsQuery
      .mutateAsync({
        user: {
          email: data.email,
        },
      })
      .then(() => {
        toast.success(t("confirmEmail.confirmationEmailSent"));
        navigate("/");
      });
  };

  return (
    <>
      <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8 bg-delicate-50">
        <div className="w-full justify-items-end bg-delicate-50 sm:block">
          <LanguageSelectorModalButton />
        </div>
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img
            alt="Memoirs logo"
            src={logo.logoGrey}
            className="mx-auto h-20 w-auto"
          />
          <h2 className="mt-6 font-poppins text-center text-2xl/9 font-bold tracking-tight text-slate-100">
            {isResendingMode
              ? t("confirmEmail.resendEmail")
              : t("confirmEmail.confirmEmail")}
          </h2>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
          <div className="bg-white px-6 py-12 shadow sm:rounded-lg sm:px-12">
            {isResendingMode ? (
              <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                <div>
                  <label
                    htmlFor="email"
                    className="block font-nunito text-sm/6 font-medium text-slate-900"
                  >
                    {t("resetPassword.email")}
                  </label>
                  <div className="mt-2">
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      className="block font-nunito w-full rounded bg-white px-3 py-1.5 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-rust-400 sm:text-sm/6"
                      {...register("email", { required: true })}
                    />
                    {errors.email && (
                      <span className="text-red-500 font-nunito">
                        {t("errors.required")}
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <button
                    type="submit"
                    className="flex w-full font-nunito justify-center rounded bg-rust-400 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-sm hover:bg-rust-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rust-400"
                  >
                    {t("confirmEmail.resendEmail")}
                  </button>
                </div>
              </form>
            ) : (
              <>
                {confirmation_token === null ? (
                  <p className="text-pretty font-nunito text-lg text-center text-slate-50 sm:text-xl/8">
                    {t("confirmEmail.emailShouldBeConfirmed")}
                  </p>
                ) : (
                  <>
                    <p className="text-pretty font-nunito text-lg text-center text-slate-50 sm:text-xl/8">
                      {isConfirmed
                        ? t("confirmEmail.confirmEmailSuccess")
                        : t("confirmEmail.confirmingEmail")}
                    </p>
                    <p className="text-pretty font-nunito text-lg text-center sm:text-xl/8">
                      {!isConfirmed && (
                        <BeatLoader color={theme.colors.delicate[100]} />
                      )}
                    </p>
                  </>
                )}
              </>
            )}
          </div>

          <div className="flex items-center justify-center mt-10 gap-6">
            <p className="mt-10 text-center text-sm/6 text-slate-50 ">
              <NavLink
                to={isConfirmed ? "/login" : "/"}
                className="font-semibold font-nunito text-rust-400 hover:text-rust-50"
              >
                {isConfirmed
                  ? t("confirmEmail.backToLogin")
                  : t("confirmEmail.backToHome")}
              </NavLink>
            </p>
            {!isResendingMode && (
              <p className="mt-10 text-center text-sm/6 text-slate-50 ">
                <button
                  className="font-semibold font-nunito text-rust-400 hover:text-rust-50"
                  onClick={() => setIsResendingMode(true)}
                >
                  {t("confirmEmail.resendEmail")}
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
